import React, { useState, useEffect, useRef } from 'react';
import API_URL from '../config';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClientForm from './ClientForm';
import CSVImport from './CSVImport';
import SharedNav from './SharedNav';
import StatusLabel from './shared/StatusLabel';
import './Dashboard.css';

// Client pipeline stage tiers -- unrelated to candidate screening status, so mapped explicitly
// here rather than guessed by the shared classifyStatusTier keyword matcher.
const CLIENT_STATUS_TIER = { lead: 'info', closed: 'pass', onboard: 'warn', nrlv: 'fail' };

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedClients, setSelectedClients] = useState([]);
  const [showAssignMenu, setShowAssignMenu] = useState(false);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { clientId, field }
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);   // { clientId, field }
  const [errorMessage, setErrorMessage] = useState('');

  // Ref to prevent double-save (e.g. Enter key triggers blur)
  const cancelEditRef = useRef(false);

  const isAdmin = user.role?.toLowerCase() === 'admin';

  useEffect(() => {
    fetchClients();
    fetchCompanies();
    fetchStations();
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);


  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchStations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/stations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStations(response.data);
    } catch (err) {
      console.error('Error fetching stations:', err);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedClients(filteredAndSortedClients.map(c => c.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (id) => {
    setSelectedClients(prev => {
      if (prev.includes(id)) {
        return prev.filter(cid => cid !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleAssignToUser = async (userId) => {
    if (selectedClients.length === 0) {
      alert('Please select clients to assign');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/clients/assign`,
        { clientIds: selectedClients, userId: userId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedClients([]);
      setShowAssignMenu(false);
      fetchClients();
    } catch (err) {
      alert('Error assigning clients');
    }
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setShowForm(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClients();
    } catch (err) {
      alert('Error deleting client');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingClient(null);
    fetchClients();
  };

  const handleImportClose = () => {
    setShowImport(false);
  };

  const handleImportComplete = () => {
    fetchClients();
  };

  const handleExport = () => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/clients/export/csv?token=${token}`, '_blank');
  };

  // ── Inline editing ────────────────────────────────────────────────────────

  const isEditingCell = (clientId, field) =>
    editingCell?.clientId === clientId && editingCell?.field === field;

  const isSavingCell = (clientId, field) =>
    savingCell?.clientId === clientId && savingCell?.field === field;

  const handleCellClick = (clientId, field, currentValue) => {
    if (editingCell?.clientId === clientId && editingCell?.field === field) return;
    cancelEditRef.current = false;
    setEditingCell({ clientId, field });
    setEditingValue(currentValue ?? '');
  };

  const saveCellValue = async (clientId, field, newValue) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    const isEmpty = v => v == null || v === '';
    if (isEmpty(newValue) && isEmpty(client[field])) return;
    if (newValue === client[field]) return;

    setSavingCell({ clientId, field });
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/clients/${clientId}`,
        { ...client, [field]: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, [field]: newValue } : c
      ));
    } catch (err) {
      console.error('Error saving cell:', err);
      setErrorMessage('Failed to save. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setSavingCell(null);
    }
  };

  const handleCellSave = (clientId, field) => {
    const newValue = editingValue;
    setEditingCell(null);
    saveCellValue(clientId, field, newValue);
  };

  const handleCellKeyDown = (e, clientId, field) => {
    if (e.key === 'Enter' && field !== 'call_notes' && field !== 'recruiting_start_tasks') {
      e.preventDefault();
      e.target.blur(); // triggers onBlur → handleCellSave
    } else if (e.key === 'Escape') {
      cancelEditRef.current = true;
      setEditingCell(null);
      setEditingValue('');
    }
  };

  // onBlur handler for all inputs — skipped if escape/select-change already handled it
  const makeCellBlurHandler = (clientId, field) => () => {
    if (cancelEditRef.current) {
      cancelEditRef.current = false;
      return;
    }
    handleCellSave(clientId, field);
  };

  // onChange handler for select fields — saves immediately on selection
  // For ID fields (company_id, station_id) the select value is always a string; convert to int
  const makeSelectChangeHandler = (clientId, field) => (e) => {
    const raw = e.target.value;
    const v = (field === 'company_id' || field === 'station_id')
      ? (raw ? parseInt(raw, 10) : null)
      : raw;
    cancelEditRef.current = true; // prevent onBlur from firing a second save
    setEditingValue(v);
    setEditingCell(null);
    saveCellValue(clientId, field, v);
  };

  const renderCell = (client, field) => {
    const clientId = client.id;

    if (isEditingCell(clientId, field)) {
      const sharedProps = {
        value: editingValue,
        onChange: (e) => setEditingValue(e.target.value),
        onBlur: makeCellBlurHandler(clientId, field),
        onKeyDown: (e) => handleCellKeyDown(e, clientId, field),
        autoFocus: true,
        onClick: (e) => e.stopPropagation(),
      };

      if (field === 'linehaul_or_pad') {
        return (
          <select
            {...sharedProps}
            className="cell-input"
            onChange={makeSelectChangeHandler(clientId, field)}
            onBlur={makeCellBlurHandler(clientId, field)}
          >
            <option value="">--</option>
            <option value="Linehaul">Linehaul</option>
            <option value="P&D">P&D</option>
          </select>
        );
      }

      if (field === 'status') {
        return (
          <select
            {...sharedProps}
            className="cell-input"
            onChange={makeSelectChangeHandler(clientId, field)}
            onBlur={makeCellBlurHandler(clientId, field)}
          >
            <option value="">--</option>
            <option value="Lead">Lead</option>
            <option value="Closed">Closed</option>
            <option value="Onboard">Onboard</option>
            <option value="NRLV">NRLV</option>
          </select>
        );
      }

      if (field === 'company_id') {
        return (
          <select
            {...sharedProps}
            className="cell-input"
            onChange={makeSelectChangeHandler(clientId, field)}
            onBlur={makeCellBlurHandler(clientId, field)}
          >
            <option value="">--</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        );
      }

      if (field === 'contact_status') {
        return (
          <select
            {...sharedProps}
            className="cell-input"
            onChange={makeSelectChangeHandler(clientId, field)}
            onBlur={makeCellBlurHandler(clientId, field)}
          >
            <option value="">--</option>
            <option value="Not Contacted">Not Contacted</option>
            <option value="No Response">No Response</option>
            <option value="Not Interested">Not Interested</option>
            <option value="Not a Fit">Not a Fit</option>
            <option value="Follow Up">Follow Up</option>
          </select>
        );
      }

      if (field === 'follow_up_date') {
        return <input type="date" {...sharedProps} className="cell-input" />;
      }

      if (field === 'call_notes' || field === 'recruiting_start_tasks') {
        return (
          <textarea
            {...sharedProps}
            className="cell-textarea"
            rows={3}
          />
        );
      }

      if (field === 'call_date' || field === 'onboarding_date') {
        return <input type="date" {...sharedProps} className="cell-input" />;
      }

      if (field === 'email') {
        return <input type="email" {...sharedProps} className="cell-input" />;
      }

      return <input type="text" {...sharedProps} className="cell-input" />;
    }

    if (isSavingCell(clientId, field)) {
      return <span className="cell-saving">{client[field]}</span>;
    }

    return client[field];
  };

  // ── Filters & sorting ─────────────────────────────────────────────────────

  let filteredClients = clients.filter(client => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !search || (
      client.name?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.company?.toLowerCase().includes(search) ||
      client.phone_number?.toLowerCase().includes(search)
    );

    if (!matchesSearch) return false;

    return true;
  });

  let filteredAndSortedClients = [...filteredClients];
  if (sortConfig.key) {
    filteredAndSortedClients.sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  const getUserNameById = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? foundUser.email : 'Unassigned';
  };

  return (
    <div className="dashboard">
      <SharedNav user={user} onLogout={onLogout} />

      {errorMessage && (
        <div className="error-toast">{errorMessage}</div>
      )}

      <div className="dashboard-content">
        <div className="controls">
          <input
            type="text"
            className="search-box"
            placeholder="Global search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="control-buttons">
            {selectedClients.length > 0 && (
              <div className="assign-dropdown">
                <button
                  onClick={() => setShowAssignMenu(!showAssignMenu)}
                  className="btn-secondary"
                >
                  Assign ({selectedClients.length}) ▼
                </button>
                {showAssignMenu && (
                  <div className="dropdown-menu">
                    <button onClick={() => handleAssignToUser(null)}>
                      Unassign
                    </button>
                    {users.map(u => (
                      <button key={u.id} onClick={() => handleAssignToUser(u.id)}>
                        {u.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isAdmin && (
              <>
                <button onClick={() => setShowImport(true)} className="btn-secondary">
                  Import CSV
                </button>
                <button onClick={handleExport} className="btn-secondary">
                  Export CSV
                </button>
              </>
            )}
            <button onClick={handleAddClient} className="btn-primary">
              + Add Client
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading clients...</div>
        ) : (
          <div className="table-container mtn-table-shell">
          <div className="mtn-scroll-x">
            <table className="clients-table sortable">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={selectedClients.length === filteredAndSortedClients.length && filteredAndSortedClients.length > 0}
                    />
                  </th>
                  <th onClick={() => handleSort('created_at')}>
                    Date Added {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('name')}>
                    Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('service_type')}>
                    Service Type {sortConfig.key === 'service_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('linehaul_or_pad')}>
                    Linehaul/P&D {sortConfig.key === 'linehaul_or_pad' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('contact_status')}>
                    Contact Status {sortConfig.key === 'contact_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('call_date')}>
                    Call Date {sortConfig.key === 'call_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('follow_up_date')}>
                    Follow Up Date {sortConfig.key === 'follow_up_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('phone_number')}>
                    Phone {sortConfig.key === 'phone_number' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('company')}>
                    Company {sortConfig.key === 'company' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('current_provider')}>
                    Current Provider {sortConfig.key === 'current_provider' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('state')}>
                    State {sortConfig.key === 'state' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('city')}>
                    City {sortConfig.key === 'city' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('station')}>
                    Station {sortConfig.key === 'station' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('location')}>
                    Location {sortConfig.key === 'location' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('current_status')}>
                    Current {sortConfig.key === 'current_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('status')}>
                    Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('call_notes')}>
                    Call Notes {sortConfig.key === 'call_notes' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('onboarding_date')}>
                    Onboarding Date {sortConfig.key === 'onboarding_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('recruiting_start_tasks')}>
                    Recruiting Tasks {sortConfig.key === 'recruiting_start_tasks' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('assigned_to_user_id')}>
                    Assigned To {sortConfig.key === 'assigned_to_user_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedClients.map((client) => (
                  <tr key={client.id} className={selectedClients.includes(client.id) ? 'selected' : ''}>
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={() => handleSelectClient(client.id)}
                      />
                    </td>

                    <td className="date-cell">
                      {client.created_at ? new Date(client.created_at).toLocaleDateString() : ''}
                    </td>

                    <td className="name-cell editable" onClick={() => handleCellClick(client.id, 'name', client.name)}>
                      {renderCell(client, 'name')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'service_type', client.service_type)}>
                      {renderCell(client, 'service_type')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'linehaul_or_pad', client.linehaul_or_pad)}>
                      {renderCell(client, 'linehaul_or_pad')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'contact_status', client.contact_status)}>
                      {isEditingCell(client.id, 'contact_status')
                        ? renderCell(client, 'contact_status')
                        : (
                          <span className={'contact-badge contact-' + (client.contact_status || 'not-contacted').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}>
                            {client.contact_status || 'Not Contacted'}
                          </span>
                        )}
                    </td>

                    <td className="date-cell editable" onClick={() => handleCellClick(client.id, 'call_date', client.call_date)}>
                      {renderCell(client, 'call_date')}
                    </td>

                    <td className="date-cell editable" onClick={() => handleCellClick(client.id, 'follow_up_date', client.follow_up_date)}>
                      {renderCell(client, 'follow_up_date')}
                    </td>

                    <td
                      className="masked-data editable"
                      onClick={() => handleCellClick(client.id, 'phone_number', client.phone_number)}
                    >
                      {isEditingCell(client.id, 'phone_number')
                        ? renderCell(client, 'phone_number')
                        : (client.phone_number ? '•••-•••-••••' : '')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'company_id', client.company_id)}>
                      {isEditingCell(client.id, 'company_id')
                        ? renderCell(client, 'company_id')
                        : client.company}
                    </td>

                    <td
                      className="email-cell masked-data editable"
                      onClick={() => handleCellClick(client.id, 'email', client.email)}
                    >
                      {isEditingCell(client.id, 'email')
                        ? renderCell(client, 'email')
                        : (client.email ? '••••••@••••.com' : '')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'current_provider', client.current_provider)}>
                      {renderCell(client, 'current_provider')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'state', client.state)}>
                      {renderCell(client, 'state')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'city', client.city)}>
                      {renderCell(client, 'city')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'station', client.station)}>
                      {renderCell(client, 'station')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'location', client.location)}>
                      {renderCell(client, 'location')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'current_status', client.current_status)}>
                      {renderCell(client, 'current_status')}
                    </td>

                    <td className="editable" onClick={() => handleCellClick(client.id, 'status', client.status)}>
                      {isEditingCell(client.id, 'status')
                        ? renderCell(client, 'status')
                        : <StatusLabel value={client.status} tier={CLIENT_STATUS_TIER[client.status?.toLowerCase()] || 'neutral'} />}
                    </td>

                    <td
                      className="notes-cell editable"
                      title={isEditingCell(client.id, 'call_notes') ? undefined : client.call_notes}
                      onClick={() => handleCellClick(client.id, 'call_notes', client.call_notes)}
                    >
                      {renderCell(client, 'call_notes')}
                    </td>

                    <td className="date-cell editable" onClick={() => handleCellClick(client.id, 'onboarding_date', client.onboarding_date)}>
                      {renderCell(client, 'onboarding_date')}
                    </td>

                    <td
                      className="notes-cell editable"
                      title={isEditingCell(client.id, 'recruiting_start_tasks') ? undefined : client.recruiting_start_tasks}
                      onClick={() => handleCellClick(client.id, 'recruiting_start_tasks', client.recruiting_start_tasks)}
                    >
                      {renderCell(client, 'recruiting_start_tasks')}
                    </td>

                    <td className="assigned-col">
                      {client.assigned_to_user_id ? getUserNameById(client.assigned_to_user_id) : 'Unassigned'}
                    </td>

                    <td className="actions-cell">
                      <button
                        onClick={() => handleEditClient(client)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            <div className="table-footer">
              Showing {filteredAndSortedClients.length} of {clients.length} clients
              {selectedClients.length > 0 && ' (' + selectedClients.length + ' selected)'}
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <ClientForm
          client={editingClient}
          onClose={handleFormClose}
        />
      )}

      {showImport && (
        <CSVImport
          onClose={handleImportClose}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
}

export default Dashboard;
